import Constants from "../../../utils/constants";
import React from "react";
import { round } from "../../../utils/tools";
import dayjs from "dayjs";

import Detail from "./Detail";
import Simple from "./Simple";

const styles = {
  document: {
    color: "#000000",
    fontFamily: "sans-serif",
    fontSize: "9pt",
  },
  logo: {
    marginBottom: "10pt",
    textAlign: "center",
  },
  addressTable: {
    border: "none",
    borderCollapse: "collapse",
    marginBottom: "16pt",
    width: "100%",
  },
  addressCell: {
    border: "none",
    padding: "2pt 4pt",
    verticalAlign: "top",
  },
  title: {
    fontSize: "16pt",
    margin: "0 0 14pt",
    textAlign: "center",
  },
  metadata: {
    marginBottom: "4pt",
    textAlign: "left",
  },
  lots: {
    marginTop: "18pt",
    marginBottom: "12pt",
    textAlign: "left",
  },
  sectionTable: {
    borderCollapse: "collapse",
    fontSize: "8pt",
    marginBottom: "16pt",
    width: "100%",
  },
  sectionHeader: {
    backgroundColor: "#8eaadb",
    border: "1px solid #D3D3D3",
    padding: "4pt",
    textAlign: "center",
    verticalAlign: "middle",
  },
  columnHeader: {
    backgroundColor: "#e6e6e6",
    border: "1px solid #D3D3D3",
    padding: "3pt",
    textAlign: "left",
    verticalAlign: "middle",
  },
  centerHeader: {
    backgroundColor: "#e6e6e6",
    border: "1px solid #D3D3D3",
    padding: "3pt",
    textAlign: "center",
    verticalAlign: "middle",
  },
  cell: {
    border: "1px solid #D3D3D3",
    padding: "3pt",
    verticalAlign: "top",
  },
  centerCell: {
    border: "1px solid #D3D3D3",
    padding: "3pt",
    textAlign: "center",
    verticalAlign: "top",
  },
  amountCell: {
    border: "1px solid #D3D3D3",
    padding: "3pt",
    textAlign: "right",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  totalLabelCell: {
    backgroundColor: "#d9e2f3",
    border: "1px solid #D3D3D3",
    padding: "3pt",
    textAlign: "right",
    verticalAlign: "top",
  },
  totalAmountCell: {
    backgroundColor: "#d9e2f3",
    border: "1px solid #D3D3D3",
    padding: "3pt",
    textAlign: "right",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
};

const formatCurrency = (value) =>
  parseFloat(value).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });

const formatCurrencyIfNonZero = (value) =>
  parseFloat(value) !== 0 ? formatCurrency(value) : null;

const emptyStr = (str) => (str ? str : "");

const AccountingSectionHeader = ({ title }) => (
  <thead>
    <tr>
      <th colSpan="4" style={styles.sectionHeader}>
        <strong>{title}</strong>
      </th>
    </tr>
    <tr>
      <th width="15%" style={styles.centerHeader}>
        <strong>DATE</strong>
      </th>
      <th width="55%" style={styles.columnHeader}>
        <strong>LIBELLE</strong>
      </th>
      <th width="15%" style={styles.centerHeader}>
        <strong>DEBIT</strong>
      </th>
      <th width="15%" style={styles.centerHeader}>
        <strong>CREDIT</strong>
      </th>
    </tr>
  </thead>
);

const RecapRow = ({ date, label, debit = 0, credit = 0 }) => (
  <tr>
    <td width="15%" style={styles.centerCell}>
      {date}
    </td>
    <td width="55%" style={styles.cell}>
      {label}
    </td>
    <td width="15%" style={styles.amountCell}>
      {formatCurrencyIfNonZero(debit)}
    </td>
    <td width="15%" style={styles.amountCell}>
      {formatCurrencyIfNonZero(credit)}
    </td>
  </tr>
);

const RelevePDF = ({ dateButoir, detaillee, tva, ligneReddition, utilisateur }) => {
    const releveDate = dateButoir
      ? dayjs(dateButoir, "DD/MM/YYYY").format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");

    let lots = [];
    let locsPrecedentes = [];
    // Suit la première location réellement affichée en mode détaillé (cf. plus bas),
    // distincte de locsPrecedentes qui suit toutes les locations traitées.
    let hasRenderedDetailLocation = false;
    let totalDebit = 0;
    let totalCredit = 0;
    let totalSolde = 0;
    let totalSoldeDebit = 0;
    let totalSoldeCredit = 0;
    let totalHonoraires = 0;
    let totalLocations = 0;
    let ssTotalDebit = 0;
    let ssTotalCredit = 0;
    let honorairesHT = 0;
    let honorairesTVA = 0;
    let total = 0;
    let totalCompta = 0;

    totalHonoraires = ligneReddition.montantFrais;

    ligneReddition.locations.forEach((ligne) => {
      totalLocations += ligne.montant;
      totalCredit += ligne.montant;
    });

    honorairesHT = round(totalHonoraires / 1.2, 2);
    honorairesTVA = totalHonoraires - honorairesHT;

    const honorairesRows =
      parseFloat(totalHonoraires) !== 0
        ? tva
          ? [
              { key: "honoraires-ht", label: "HONORAIRES HT", debit: honorairesHT },
              { key: "honoraires-tva", label: "TVA SUR HONORAIRES", debit: honorairesTVA },
            ]
          : [
              {
                key: "honoraires-gestion",
                label: "HONORAIRES DE GESTION",
                debit: totalHonoraires,
              },
            ]
        : [];

    ligneReddition.lignesEcritures
      .filter((ligne) => ligne.compta)
      .forEach((ligne, i) => {
        totalDebit += parseFloat(ligne.ecriture_ligne_montant_debit);
        totalCredit += parseFloat(ligne.ecriture_ligne_montant_credit);
        ssTotalDebit += parseFloat(ligne.ecriture_ligne_montant_debit);
        ssTotalCredit += parseFloat(ligne.ecriture_ligne_montant_credit);
      });
    totalCompta = ssTotalDebit - ssTotalCredit;
    totalDebit = totalDebit + totalHonoraires;

    totalSolde = totalCredit - totalDebit;
    if (totalSolde < 0) {
      totalSoldeDebit = totalSolde;
    } else if (totalSolde > 0) {
      totalSoldeCredit = totalSolde;
    }

    const societe = utilisateur.utilisateur_societe;

    const chemin = `${Constants.mode}://${Constants.domain}:${Constants.port}/api/societe/display/${societe.societe_id}`;

    let width = 200;
    let height = 30;

    if (societe.societe_logo_width) width = societe.societe_logo_width;
    if (societe.societe_logo_height) height = societe.societe_logo_height;

    return (
      <div style={styles.document}>
        {/* ADRESSE CABINET */}
        <div style={styles.logo}>
          <img src={chemin} width={width} height={height} alt="" />
        </div>

        {/* ADRESSE PROPRIETAIRE */}
        <div>
          <table style={styles.addressTable}>
            <tbody>
              <tr>
                <td width="55%" style={styles.addressCell}></td>
                <td width="45%" style={styles.addressCell}>
                  <strong>
                    {ligneReddition.proprietaire.personne_qualite.qualite_libelle}{" "}
                    {ligneReddition.proprietaire.personne_nom}{" "}
                    {ligneReddition.proprietaire.personne_prenom}
                  </strong>
                  <br />
                  {ligneReddition.proprietaire.personne_adresse.adresse_numero}{" "}
                  {ligneReddition.proprietaire.personne_adresse.adresse_voie}
                  <br />
                  {ligneReddition.proprietaire.personne_adresse.adresse_secteur}
                  <br />
                  {ligneReddition.proprietaire.personne_adresse.adresse_code_postal}{" "}
                  {ligneReddition.proprietaire.personne_adresse.adresse_ville}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={styles.title}>
          <strong>
            {"RELEVE DE GESTION au "}
            {releveDate}
          </strong>
        </div>
        <div style={styles.metadata}>
          {
            ligneReddition.proprietaire_affectations[0].proprietaire_affectation_societe
              .societe_adresse.adresse_ville
          }
          {", le "}
          {dayjs().format("DD/MM/YYYY")}
        </div>
        <div style={styles.metadata}>
          <i>
            {"Propriétaire n° "}
            {
              ligneReddition.proprietaire_affectations[0].proprietaire_affectation_compte
                .compte_compte_auxiliaire
            }
          </i>
        </div>
        {ligneReddition.locations.length > 0 &&
          ligneReddition.locations.map((location, i) => {
            if (!lots.some((lot) => lot.lot_id === location.location_lot.lot_id)) {
              lots.push(location.location_lot);
              return (
                <div key={i} style={styles.metadata}>
                  <i>
                    {`Lot n°${location.location_lot.lot_reference} 
              ${
                location.location_lot.lot_designation
                  ? "(" + location.location_lot.lot_designation + ")"
                  : ""
              }: 
              ${location.location_lot.lot_adresse.adresse_gmaps_adresse}`}
                  </i>
                </div>
              );
            }
            return null;
          })}
        {/* ENTETES */}
        <div style={styles.lots}>
          <table style={styles.sectionTable}>
            <thead>
              <tr>
                <th colSpan="2" style={styles.sectionHeader}>
                  <strong>LOCATIONS</strong>
                </th>
              </tr>
              <tr>
                {/*<th width="15%" style={styles.centerHeader}>
                   <strong>DATE</strong>
                </th>*/}
                <th width="85%" style={styles.columnHeader}>
                  <strong>LIBELLE</strong>
                </th>
                <th width="15%" style={styles.centerHeader}>
                  <strong>CREDIT</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {ligneReddition.locations
                .sort(
                  (a, b) =>
                    dayjs(a.location_date_debut, "YYYY-MM-DD").unix() -
                    dayjs(b.location_date_debut, "YYYY-MM-DD").unix()
                )
                .map((location, i) => {
                  if (!locsPrecedentes.some((loc) => loc.location_id === location.location_id)) {
                    total = 0;
                    const nbNuits = dayjs(location.location_date_fin).diff(
                      dayjs(location.location_date_debut),
                      "days"
                    );
                    ligneReddition.locations.forEach((item) => {
                      if (item.location_id === location.location_id) {
                        total += item.montant;
                      }
                    });
                    // Traitement des reglements
                    const regroupedReglements = ligneReddition.reglements
                      .filter(
                        (reg) =>
                          reg.reglement_quittance.quittance_location_id === location.location_id
                      )
                      .reduce((acc, reglement) => {
                        const { quittance_id, quittance_date_debut, quittance_date_fin } =
                          reglement.reglement_quittance;
                        const key = quittance_id;

                        if (!acc[key]) {
                          acc[key] = {
                            quittance_id,
                            quittance_date_debut,
                            quittance_date_fin,
                            reglement_ligne: [],
                          };
                        }

                        reglement.reglement_ligne.forEach((ligne) => {
                          const existingLigne = acc[key].reglement_ligne.find(
                            (item) =>
                              item.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                                .rubrique_id ===
                              ligne.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                                .rubrique_id
                          );

                          if (existingLigne) {
                            existingLigne.reglement_ligne_montant =
                              parseFloat(existingLigne.reglement_ligne_montant) +
                              parseFloat(ligne.reglement_ligne_montant);
                          } else {
                            acc[key].reglement_ligne.push({ ...ligne });
                          }
                        });

                        return acc;
                      }, {});

                    const result = Object.values(regroupedReglements);
                    const label = `${
                      location.location_requete &&
                      location.location_requete.requete_locataire.locataire_personne.personne_nom
                    } ${
                      location.location_requete &&
                      emptyStr(
                        location.location_requete.requete_locataire.locataire_personne
                          .personne_prenom
                      )
                    } (${location.location_lot.lot_reference})${
                      location.location_type_contrat === 1 || location.location_type_contrat === 2
                        ? ""
                        : `, du ${dayjs(location.location_date_debut).format(
                            "DD/MM/YYYY"
                          )} au ${dayjs(location.location_date_fin).format(
                            "DD/MM/YYYY"
                          )} (${nbNuits} nuits)`
                    }`;

                    locsPrecedentes.push(location);

                    if (!detaillee) {
                      // Une location dont le total ne compte que des lignes "G" (Gardée)
                      // vaut 0 : on la masque plutôt que d'afficher "0,00 €" sans intérêt.
                      if (total === 0) {
                        return null;
                      }

                      return (
                        <Simple
                          key={i}
                          label={label}
                          credit={formatCurrency(total)}
                          styles={styles}
                        />
                      );
                    }

                    // Une location sans ligne "P" ne sera pas rendue par <Detail/>
                    // (voir Detail.js). On le sait à l'avance pour ne marquer comme
                    // "isFirst" que la première location réellement affichée, et
                    // éviter un séparateur fantôme en tête de tableau.
                    const hasVisibleLines = result.some((reglement) =>
                      reglement.reglement_ligne.some(
                        (ligne) =>
                          ligne.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                            .rubrique_type === "P"
                      )
                    );
                    const isFirstLocation = !hasRenderedDetailLocation;
                    if (hasVisibleLines) {
                      hasRenderedDetailLocation = true;
                    }

                    return (
                      <Detail
                        key={i}
                        label={label}
                        reglements={result}
                        formatCurrency={formatCurrency}
                        isFirst={isFirstLocation}
                      />
                    );
                  }
                  return null;
                })}
              <tr>
                <td width="85%" style={styles.totalLabelCell}>
                  <strong>TOTAL DES VERSEMENTS : </strong>
                </td>
                <td width="15%" style={styles.totalAmountCell}>
                  {formatCurrency(totalLocations)}
                </td>
              </tr>
            </tbody>
          </table>

          {ligneReddition.lignesEcritures.length > 0 && (
            <table style={styles.sectionTable}>
              <AccountingSectionHeader title="OPÉRATIONS" />
              <tbody>
                {ligneReddition.lignesEcritures
                  .filter((ligne) => ligne.visible)
                  .map((ligne, i) => {
                    return (
                      <tr key={i}>
                        <td width="15%" style={styles.cell}>
                          {ligne.ecriture_date_ecriture}
                        </td>
                        <td width="55%" style={styles.cell}>
                          {ligne.ecriture_ligne_libelle}
                        </td>
                        <td width="15%" style={styles.amountCell}>
                          {formatCurrencyIfNonZero(ligne.ecriture_ligne_montant_debit)}
                        </td>
                        <td width="15%" style={styles.amountCell}>
                          {formatCurrencyIfNonZero(ligne.ecriture_ligne_montant_credit)}
                        </td>
                      </tr>
                    );
                  })}
                <tr>
                  <td colSpan="2" width="70%" style={styles.totalLabelCell}>
                    <strong>TOTAL : </strong>
                  </td>
                  <td width="15%" style={styles.totalAmountCell}>
                    {formatCurrencyIfNonZero(ssTotalDebit)}
                  </td>
                  <td width="15%" style={styles.totalAmountCell}>
                    {formatCurrencyIfNonZero(ssTotalCredit)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          <table style={styles.sectionTable}>
            <AccountingSectionHeader title="RECAPITULATIF" />
            <tbody>
              <RecapRow date={releveDate} label="TOTAL LOCATIONS" credit={totalLocations} />
              {ligneReddition.lignesEcritures.length > 0 && (
                <RecapRow
                  date={releveDate}
                  label="TOTAL OPÉRATIONS"
                  debit={parseFloat(totalCompta) > 0 ? totalCompta : 0}
                  credit={parseFloat(totalCompta) < 0 ? Math.abs(totalCompta) : 0}
                />
              )}
              {honorairesRows.map(({ key, label, debit }) => (
                <RecapRow key={key} date={releveDate} label={label} debit={debit} />
              ))}
              <tr>
                <td colSpan="2" width="70%" style={styles.totalLabelCell}>
                  <strong>SOLDE A {totalSoldeDebit < 0 ? "NOUS" : "VOUS"} DEVOIR :</strong>
                </td>
                <td width="15%" style={styles.totalAmountCell}>
                  {" "}
                  {formatCurrencyIfNonZero(Math.abs(totalSoldeDebit))}
                </td>
                <td width="15%" style={styles.totalAmountCell}>
                  <strong>
                    {" "}
                    {formatCurrencyIfNonZero(totalSoldeCredit)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  );
};

export default RelevePDF;
